/* =============================================================
   Psychological Safety Assessment — constants.

   Based on Timothy R. Clark's "The 4 Stages of Psychological
   Safety" (Berrett-Koehler, 2020) and the LeaderFactor practice
   guide. See research-design-memo.md for full rationale on the
   BANDS / THEMES / narrative templates.

   Item IDs are stable. Format: "<stageId>:<itemIdx>", e.g.
   "inclusion:0". Once published, never renumber — the responses
   table keys directly on these.
   ============================================================ */

export type StageId = 'inclusion' | 'learner' | 'contributor' | 'challenger';

export interface Stage {
  id: StageId;
  name: string;
  subtitle: string;
  description: string;
  /** Semantic accent color preserved from the LeaderFactor framing. */
  accent: string;
  items: string[];
}

export const STAGES: Stage[] = [
  {
    id: 'inclusion',
    name: 'Inclusion Safety',
    subtitle: 'The need to be accepted',
    description:
      'The freedom to be present as a human being — to be seen, acknowledged, and accepted for who you are.',
    accent: '#c9633a',
    items: [
      'teach inclusion as a human need and right',
      'introduce yourself at the first opportunity',
      "learn people's names and how to pronounce them",
      'physically face people when interacting',
      'listen and pause before responding',
      'ask twice as much as you tell',
      'meet people in their physical space',
      'conduct "hop on, hop off" tours to include newcomers',
      'move to mutual discovery quickly',
      'avoid comparisons and competitions',
      'create deep bonding opportunities',
      'define and communicate the purpose and values of your team',
      'identify negative bias in yourself or your team',
      'check alignment with team members',
      'create connecting rituals',
      'share your story and learn theirs',
      'make yourself available and interruptible',
      'avoid flaunting perks or status symbols',
      'follow through on small commitments',
      'create opportunities to socialize outside of work',
      'avoid hiding behind title, position, or authority',
      'avoid exclusive patterns of social interaction',
      'forbid personal attacks',
      'respond to messages promptly',
      'control your nonverbal cues',
      'avoid self-serving references to status',
      'use appropriate humor as a relief valve',
      'ask about needs and challenges',
      'ask for feedback and help',
      'express gratitude and appreciation',
      'avoid digital addiction and overdose',
      'reinforce inclusion daily',
      'conduct frequent, brief touch points',
      'show up on time',
    ],
  },
  {
    id: 'learner',
    name: 'Learner Safety',
    subtitle: 'The need to learn and grow',
    description:
      'The freedom to engage in the discovery process — to ask questions, give and receive feedback, experiment, and make mistakes.',
    accent: '#5b7a52',
    items: [
      'make learning a collaboration, not a competition',
      'adopt a learning mindset',
      'assess the learning style and disposition of each person',
      'encourage both your private and public learners',
      "share what you're learning",
      'help your team members get small wins',
      'invite others to think beyond their roles',
      'share past mistakes openly',
      'ask for help from those of lower status',
      'frame problems before solving them',
      'share your personal learning goals',
      'help team members set concrete, time-bound learning goals',
      'encourage the emotional side of learning to build confidence',
      'formulate a set of questions before meetings',
      'ask questions to activate learning',
      'implement rotating mentoring',
      'assign action learning projects',
      'destigmatize failure by celebrating it',
      'dedicate time to zero-budget learning',
      'admit your ignorance and say "I don\'t know"',
      'demonstrate learning empathy',
      'match the problem to the person and the reasons',
      'create a learning club',
      'conduct a daily learning burst',
      'communicate a vision of potential',
      'explain formal vs. informal learning',
      'model an example of taking notes',
      'debottleneck learning on your team',
      'embrace quality feedback regardless of where it comes from',
      'share what you unlearn',
      'ask for feedback at the moment of need',
      'never stand still in learning',
      'provide unwavering support through low points',
      'journal your learning observations',
      'avoid going on "educational welfare"',
      'teach the concept of learning agility',
      'pin your star on learning, not on a company',
    ],
  },
  {
    id: 'contributor',
    name: 'Contributor Safety',
    subtitle: 'The need to make a difference',
    description:
      'The freedom to apply your judgment, use your skills, and contribute meaningfully to outcomes.',
    accent: '#2c6e8f',
    items: [
      'rotate the conducting of meetings',
      'clarify roles on the team',
      'recognize accomplishment',
      'avoid correcting with anger, blame, or shame',
      'identify stall points in work',
      'celebrate small wins',
      'shift from tell to ask',
      'share your values',
      'share your workstyle and communication preferences',
      'set ground rules',
      'create conditions for peak engagement',
      'align work with passion',
      'shape the experience for team members',
      'give stretch assignments',
      'avoid boredom and burnout',
      'distinguish ability vs. motivation problems',
      'own your own contribution',
      'help others see their strengths',
      'give people the why',
      'paint the vision',
      'keep focused with tradeoffs',
      'remove a sense of entitlement',
      'make the team customer-centric',
      'let team members do it their way',
      'measure performance',
      'approach failure with curiosity rather than criticism',
      'clarify the decision-making process',
      'compliment participation from quiet team members',
      'avoid shutdown statements',
      'report your own mistakes and errors',
      'ask people what they think',
      'check skills and resources before assigning work',
      'accept bad news without punishing the messenger',
      'reward those who accept additional responsibility',
      'create outcome accountability',
      'teach the compounding principle',
    ],
  },
  {
    id: 'challenger',
    name: 'Challenger Safety',
    subtitle: 'The need to make things better',
    description:
      'The freedom to challenge the status quo, voice dissent, and offer candid feedback without retaliation.',
    accent: '#7a3b6e',
    items: [
      'take your finger off the fear button',
      'assign dissent in discussions',
      'encourage others to think beyond their roles',
      'respond constructively to disruptive ideas and bad news',
      'when you reject feedback, explain why',
      'weigh in last in discussions',
      'display no pride of authorship',
      'model vulnerability',
      'reward vulnerability in others',
      'reward shots on goal, not just wins',
      'mandate a no-interruption rule',
      'define what is in and out of scope',
      'create diverse teams',
      'take a break before a breakdown',
      'identify and avoid defensive routines',
      'ask for bad news',
      'tee up "challenge-the-status-quo" questions',
      'praise quantity over quality in brainstorming',
      'challenge your own decisions',
      'look for R.O.T. — Redundant, Obsolete, Trivial',
      'teach deliberate vs. emergent strategy',
      'model the art of disagreement',
      'share challenge experiences',
      'identify tangible and intangible sources of value',
      'recognize patterns in problems',
      'put a hypothesis on the table',
      'protect your team from groupthink',
      'scan the environment for adaptive challenges',
      'identify inflection points',
      'engage in recombination of ideas',
      'hold a "do-nothing scenario" session',
      'identify your status quo bias',
      'come with dumb questions and raggedy solutions',
      'respect local knowledge',
      'bring in outsiders for fresh perspective',
      'follow the disruption question sequence',
    ],
  },
];

export const SCALE = [
  { value: 1, label: 'Never' },
  { value: 2, label: 'Rarely' },
  { value: 3, label: 'Sometimes' },
  { value: 4, label: 'Often' },
  { value: 5, label: 'Always' },
] as const;

// ============================================================
// Bands (per research-design-memo §3 — adapted from LeaderFactor zones)
// ============================================================
export type BandKey =
  | 'exemplary'
  | 'strong'
  | 'developing'
  | 'inconsistent'
  | 'underdeveloped'
  | 'significant_gap';

export interface Band {
  min: number;       // inclusive
  max: number;       // exclusive
  key: BandKey;
  label: string;
  zone: string;
  tone: string;      // semantic color (red/neutral/blue family)
}

export const BANDS: Band[] = [
  { min: 4.5, max: 5.01, key: 'exemplary',       label: 'Exemplary',       zone: 'Deep Blue',     tone: '#1e6f3a' },
  { min: 4.0, max: 4.5,  key: 'strong',          label: 'Strong',          zone: 'Blue',          tone: '#2e7d4f' },
  { min: 3.5, max: 4.0,  key: 'developing',      label: 'Developing',      zone: 'Upper Neutral', tone: '#7a6f3a' },
  { min: 3.0, max: 3.5,  key: 'inconsistent',    label: 'Inconsistent',    zone: 'Lower Neutral', tone: '#a07338' },
  { min: 2.5, max: 3.0,  key: 'underdeveloped',  label: 'Underdeveloped',  zone: 'Upper Red',     tone: '#b94d33' },
  { min: 0.0, max: 2.5,  key: 'significant_gap', label: 'Significant gap', zone: 'Lower Red',     tone: '#8b2e1e' },
];

// ============================================================
// Per-stage narrative paragraphs (research-design-memo §7)
// ============================================================
export const STAGE_NARRATIVES: Record<StageId, Record<BandKey, string>> = {
  inclusion: {
    exemplary:       'You make people feel they belong before they have to prove anything. This is the foundation that lets everything above it stand.',
    strong:          'You reliably create the conditions for people to feel included. The small daily acts — names, eye contact, asking before telling — are mostly in place.',
    developing:      "You include people well when you're paying attention, but the practice isn't yet automatic. People around you can probably tell when you're distracted, and that becomes data about whether they belong.",
    inconsistent:    "Inclusion happens, but it isn't patterned. Without consistency, people around you can't be sure whether they're welcome — and that uncertainty is itself exclusion.",
    underdeveloped:  "The behaviours that signal “you belong here” are running below what people need to feel safe being themselves at work. This is the foundation; until it's solid, nothing above it is structurally sound.",
    significant_gap: 'Belonging is largely being communicated by absence. People are probably reading silence as exclusion. Begin here, before anything else.',
  },
  learner: {
    exemplary:       'You actively reward people for not-knowing. Curiosity is the default mode around you, and mistakes become information rather than evidence.',
    strong:          'You consistently signal that learning is welcome. People around you are more likely to ask a question than hide one.',
    developing:      "You support learning when you remember to, but the day-to-day fabric — the small rewards for admitting confusion or trying something new — isn't yet woven through your interactions.",
    inconsistent:    'Learning is sometimes celebrated and sometimes treated as an inefficiency. That ambiguity teaches people to be cautious about which questions they ask in front of you.',
    underdeveloped:  "Curiosity is paying a tax around you. The cost of asking a “dumb” question or admitting a mistake feels too high to most people most of the time.",
    significant_gap: 'Learning is being suppressed in your environment. People are likely hiding gaps, faking competence, and avoiding the experiments that would help them grow.',
  },
  contributor: {
    exemplary:       "You actively draw out people's contributions and give credit for them. Your team feels useful, trusted, and seen.",
    strong:          'You give people room to contribute their way and recognize what they bring. People around you are likely to lean in rather than coast.',
    developing:      'You make space for contribution, but probably narrow the path more than you realize — defaulting to your own approach, your own pace, your own definition of done.',
    inconsistent:    'Some people contribute fully around you; others have learned to wait and read the room. That asymmetry usually traces to whose contributions get rewarded vs. corrected.',
    underdeveloped:  'Contribution is being micromanaged, ignored, or corrected away. People are likely doing the minimum to stay safe and saving their real thinking for elsewhere.',
    significant_gap: 'People near you have stopped contributing meaningfully. They may be present and busy, but their judgment, ideas, and discretionary effort are being withheld.',
  },
  challenger: {
    exemplary:       "You actively reward people for telling you things you don't want to hear. The status quo is genuinely contestable around you, and that's where innovation comes from.",
    strong:          'You consistently make it safe to push back, including pushback aimed at you. People know that disagreeing with you is not a career-limiting move.',
    developing:      "You're open to challenge in principle, but the day-to-day practice — inviting dissent, weighing in last, rewarding bad news — isn't yet consistent enough that people feel safe doing it without testing the waters first.",
    inconsistent:    "Some people challenge you; others have learned the topics or moods to avoid. The information you're getting is filtered by who's willing to risk what.",
    underdeveloped:  "Dissent has gone underground. The decisions you make are happening with most of the contradictory information you'd actually want already removed.",
    significant_gap: 'Challenge has been bred out of your environment. You may be the last person in the room to find out something is broken.',
  },
};

// ============================================================
// Composite headlines & bodies (research-design-memo §6)
// ============================================================
export const COMPOSITE_HEADLINES: Record<BandKey, string> = {
  exemplary:       "A practitioner's profile.",
  strong:          'A strong foundation, with room to deepen.',
  developing:      'Mostly there, but not yet consistent.',
  inconsistent:    'An inconsistent pattern — and inconsistency is its own signal.',
  underdeveloped:  'More work to do than the surface suggests.',
  significant_gap: 'Begin with the foundation.',
};

export const COMPOSITE_BODIES: Record<BandKey, string> = {
  exemplary:       'You consistently practice the behaviours the framework identifies as safety-producing across all four stages. The work now is depth, modeling for others, and noticing the few specific behaviours below your own average.',
  strong:          'You reliably create the conditions for psychological safety in most of your interactions. The next move is consistency under pressure — the moments when these behaviours are hardest are the moments they matter most.',
  developing:      'You practice these behaviours often, but not yet patterned enough that people around you can predict the response their vulnerability will receive. Predictability is what turns occasional safety into a culture.',
  inconsistent:    'Your behaviours sit close to the neutral zone, where vulnerability is sometimes rewarded and sometimes not. This produces doubt — and doubt produces self-censorship. The fix is consistency, not intensity.',
  underdeveloped:  "More acts of vulnerability in your environment are going unrewarded than rewarded. This is not about being a bad person — it's about a pattern that, if continued, teaches people around you to retreat.",
  significant_gap: "The behaviours that produce psychological safety are largely absent from your reported daily practice. This is the starting line, and it's worth treating as such — with a small number of behaviours, practiced deliberately, before broadening out.",
};

// ============================================================
// Cross-stage themes (research-design-memo §4)
// ============================================================
export type ThemeId = 'modeling' | 'rewarding' | 'voice' | 'status' | 'failure';

export interface Theme {
  id: ThemeId;
  label: string;
  short: string;
  description: string;
}

export const THEMES: Theme[] = [
  { id: 'modeling',  label: 'Modeling vulnerability',  short: 'Going first',
    description: 'Behaviours where you make yourself vulnerable first — sharing mistakes, admitting uncertainty, asking for help. The central mechanism for granting others permission to do the same.' },
  { id: 'rewarding', label: 'Rewarding vulnerability', short: 'Responding well',
    description: 'How you respond when others take risks. The single highest-leverage class of behaviours — how you respond to one act of vulnerability shapes every future one.' },
  { id: 'voice',     label: 'Voice & inquiry',         short: 'Ask more than tell',
    description: 'Behaviours that reduce talk-time asymmetry and pull thinking out of others rather than push thinking onto them.' },
  { id: 'status',    label: 'Status leveling',         short: 'Reduce distance',
    description: 'Behaviours that lower the perceived cost of speaking up to people with more authority, seniority, or visibility than you.' },
  { id: 'failure',   label: 'Failure as information',  short: 'Mistakes as data',
    description: 'Behaviours that treat errors, bad news, and dissent as information rather than as character judgments.' },
];

/** Map of "<stageId>:<itemIdx>" -> list of theme ids. */
export const ITEM_THEMES: Record<string, ThemeId[]> = {
  // ===== INCLUSION =====
  'inclusion:5':  ['voice'],
  'inclusion:13': ['voice'],
  'inclusion:15': ['modeling'],
  'inclusion:17': ['status'],
  'inclusion:20': ['status'],
  'inclusion:21': ['status'],
  'inclusion:22': ['failure', 'rewarding'],
  'inclusion:25': ['status'],
  'inclusion:27': ['voice'],
  'inclusion:28': ['modeling', 'voice'],
  'inclusion:29': ['rewarding'],
  // ===== LEARNER =====
  'learner:4':   ['modeling'],
  'learner:7':   ['modeling', 'failure'],
  'learner:8':   ['status', 'voice'],
  'learner:14':  ['voice'],
  'learner:17':  ['failure', 'rewarding'],
  'learner:19':  ['modeling'],
  'learner:20':  ['rewarding'],
  'learner:28':  ['rewarding', 'status'],
  'learner:29':  ['modeling'],
  'learner:30':  ['voice'],
  // ===== CONTRIBUTOR =====
  'contributor:3':  ['failure', 'rewarding'],
  'contributor:6':  ['voice'],
  'contributor:7':  ['modeling'],
  'contributor:25': ['failure', 'rewarding'],
  'contributor:27': ['rewarding', 'status'],
  'contributor:28': ['rewarding'],
  'contributor:29': ['modeling', 'failure'],
  'contributor:30': ['voice'],
  'contributor:32': ['failure', 'rewarding'],
  'contributor:33': ['rewarding'],
  // ===== CHALLENGER =====
  'challenger:0':  ['failure'],
  'challenger:3':  ['rewarding', 'failure'],
  'challenger:5':  ['status', 'voice'],
  'challenger:6':  ['status', 'modeling'],
  'challenger:7':  ['modeling'],
  'challenger:8':  ['rewarding'],
  'challenger:9':  ['rewarding'],
  'challenger:15': ['failure', 'voice'],
  'challenger:16': ['voice'],
  'challenger:18': ['modeling'],
  'challenger:21': ['modeling'],
  'challenger:33': ['status'],
};

/** Total number of items across all stages. Useful for progress %. */
export const TOTAL_ITEMS = STAGES.reduce((n, s) => n + s.items.length, 0);

/** Convenience: { stageId: itemCount } */
export const ITEMS_PER_STAGE: Record<StageId, number> = STAGES.reduce(
  (acc, s) => ({ ...acc, [s.id]: s.items.length }),
  {} as Record<StageId, number>,
);
